import { useState, useCallback } from 'react';
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LogIn, LogOut, Wifi, ShieldCheck, MapPin } from 'lucide-react';

import { TimeOfDayHero, TimeOfDayControl } from '@components/scene/TimeOfDayHero';
import { useTimeOfDay } from '@components/scene/useTimeOfDay';
import { PlatformCard } from '@components/ui/PlatformCard';
import { SectionPanel } from '@components/ui/SectionPanel';
import { PillarsBar }   from '@components/ui/PillarsBar';
import { PLATFORMS, getAccessiblePlatforms } from '@config/platforms';
import { useAuth }      from '@contexts/AuthContext';
import { accessLineFromAuth } from '@utils/stationScope';
import logoWordmark from '../assets/logo-glk-wordmark.png';

// The wordmark is transparent artwork, shown as it is. A thin light edge keeps its
// green and blue legible over the dusk sky without putting a box behind it.
const LOGO_EDGE = [
  'drop-shadow(.8px 0 0 rgba(255,255,255,.8))',
  'drop-shadow(-.8px 0 0 rgba(255,255,255,.8))',
  'drop-shadow(0 .8px 0 rgba(255,255,255,.8))',
  'drop-shadow(0 -.8px 0 rgba(255,255,255,.8))',
  'drop-shadow(0 0 7px rgba(255,255,255,.3))',
  'drop-shadow(0 3px 14px rgba(0,0,0,.4))',
].join(' ');

function LogoMark() {
  return (
    <motion.img
      src={logoWordmark}
      alt="GroundLink Handling"
      draggable={false}
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
      className="h-24 md:h-28 w-auto select-none"
      style={{ filter: LOGO_EDGE }}
    />
  );
}

// ─── System status bar ────────────────────────────────────────────────────────
function StatusBar() {
  const stats = [
    { label: 'API', status: 'NOMINAL' },
    { label: 'DB',  status: 'NOMINAL' },
    { label: 'MQ',  status: 'NOMINAL' },
  ];
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.2 }}
      className="hidden md:flex items-center gap-4"
    >
      <Wifi size={12} className="text-gl-muted" />
      {stats.map(({ label, status }) => (
        <div key={label} className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-slow motion-reduce:animate-none" />
          <span className="text-2xs font-mono text-gl-muted">{label}</span>
          <span className="text-2xs font-mono" style={{ color: '#22C55E' }}>{status}</span>
        </div>
      ))}
    </motion.div>
  );
}

// ─── Main Landing Page ────────────────────────────────────────────────────────
export function Landing() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate      = useNavigate();
  const reducedMotion = useReducedMotion();
  const [hoveredId, setHoveredId] = useState(null);
  const timeOfDay = useTimeOfDay();

  const hoveredColor = hoveredId
    ? PLATFORMS.find((p) => p.id === hoveredId)?.color
    : null;

  const accessiblePlatforms = isAuthenticated && user
    ? getAccessiblePlatforms(user)
    : [];

  // Section panel state lives in the URL (?section=hr): deep-linkable,
  // refresh-safe, back-button closes the panel. Only accessible sections open.
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionId = searchParams.get('section');
  const openPlatform = sectionId
    ? accessiblePlatforms.find((p) => p.id === sectionId) || null
    : null;
  const openSection  = useCallback((id) => setSearchParams({ section: id }), [setSearchParams]);
  const closeSection = useCallback(() => setSearchParams({}), [setSearchParams]);

  const firstName  = user?.name?.split(' ')[0];
  const accessLine = isAuthenticated ? accessLineFromAuth(user) : '';

  return (
    <div className="relative min-h-screen overflow-hidden bg-gl-void">
      {/* ── Hero photo, relit by time of day ────────────────────────────────── */}
      <TimeOfDayHero phase={timeOfDay.phase} weights={timeOfDay.weights} reducedMotion={!!reducedMotion} />

      {/* ── Ambient glow: the hovered card's brand colour bleeds into the page ─ */}
      <motion.div
        className="absolute inset-0 z-[2] pointer-events-none"
        animate={{ opacity: hoveredColor ? 1 : 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        style={{
          background: hoveredColor
            ? `radial-gradient(ellipse 80% 60% at 50% 100%, ${hoveredColor}14 0%, transparent 65%)`
            : 'transparent',
        }}
      />

      {/* ── UI Layer ────────────────────────────────────────────────────────── */}
      <div className="relative z-20 flex flex-col min-h-screen">

        {/* Header */}
        <header className="flex flex-wrap items-center justify-between gap-y-3 px-4 sm:px-6 md:px-10 pt-6 pb-4">
          <LogoMark />
          <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-6">
            <StatusBar />
            {!isAuthenticated && (
              <motion.button
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                onClick={() => navigate('/login')}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-display font-medium transition-all"
                style={{
                  background: 'rgba(45,125,70,0.1)',
                  border:     '1px solid rgba(45,125,70,0.25)',
                  color:      '#2D7D46',
                }}
                whileHover={{ scale: 1.03, boxShadow: '0 0 20px rgba(45,125,70,0.2)' }}
                whileTap={{ scale: 0.97 }}
              >
                <LogIn size={14} />
                Sign In
              </motion.button>
            )}
            {isAuthenticated && (user?.role === 'super_admin' || user?.role === 'admin') && (
              <motion.button
                onClick={() => navigate('/admin/users')}
                aria-label="Users"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.35 }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-display font-medium transition-all"
                style={{ background: 'rgba(7,16,29,0.62)', border: '1px solid rgba(45,125,70,0.45)', color: '#4AAE49' }}
              >
                <ShieldCheck size={14} />
                <span className="hidden sm:inline">Users</span>
              </motion.button>
            )}
            {isAuthenticated && (user?.role === 'super_admin' || user?.role === 'admin') && (
              <motion.button
                onClick={() => navigate('/admin/stations')}
                aria-label="Stations"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-display font-medium transition-all"
                style={{ background: 'rgba(7,16,29,0.62)', border: '1px solid rgba(45,125,70,0.45)', color: '#4AAE49' }}
              >
                <MapPin size={14} />
                <span className="hidden sm:inline">Stations</span>
              </motion.button>
            )}
            {isAuthenticated && user && (
              <motion.button
                onClick={() => navigate('/account')}
                aria-label={`Account: ${user.name}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="flex items-center gap-3"
              >
                <div className="text-right hidden sm:block">
                  <p className="text-xs text-gl-ghost font-display">{user.name}</p>
                  <p className="text-2xs text-gl-muted font-mono uppercase tracking-wider">{user.role?.replace('_', ' ')}</p>
                </div>
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-display font-bold"
                  style={{ background: 'linear-gradient(135deg, #2D7D46, #1A5E9E)', color: '#0D1B2A' }}
                >
                  {user.name?.charAt(0) ?? '?'}
                </div>
              </motion.button>
            )}
            {isAuthenticated && user && (
              <motion.button
                onClick={async () => { await logout(); navigate('/login'); }}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.45 }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                title="Sign out"
                aria-label="Sign out"
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-display font-medium transition-all"
                style={{ background: 'rgba(7,16,29,0.62)', border: '1px solid rgba(200,60,60,0.35)', color: '#E06C6C' }}
              >
                <LogOut size={14} />
                <span className="hidden sm:inline">Sign Out</span>
              </motion.button>
            )}
          </div>
        </header>

        {/* Hero text */}
        <motion.div
          className="px-6 md:px-10 pt-6 md:pt-10 pb-6 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.7 }}
        >
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-display font-semibold tracking-tight text-white leading-[1.05]"
            style={{ textShadow: '0 2px 24px rgba(0,0,0,0.45)' }}
          >
            {isAuthenticated && firstName ? `Cleared for operations, ${firstName}` : 'Operations Hub'}
          </h1>
          <p className="mt-4 text-base md:text-lg text-white/90 font-light max-w-xl mx-auto leading-relaxed" style={{ textShadow: '0 1px 12px rgba(0,0,0,0.55)' }}>
            {isAuthenticated
              ? (accessLine || 'Unified access to all GroundLink Handling platforms.')
              : 'Unified access to all GroundLink Handling platforms. Sign in to access your designated areas.'}
          </p>
        </motion.div>

        {/* ── Platform cards ───────────────────────────────────────────────── */}
        <div className="mt-auto px-4 md:px-8 lg:px-12 pb-4">
          <div data-testid="platforms-grid" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 max-w-7xl mx-auto">
            {PLATFORMS.map((platform, i) => (
              <PlatformCard
                key={platform.id}
                platform={platform}
                index={i}
                compact
                onHoverChange={setHoveredId}
                onOpen={openSection}
                accessible={
                  !isAuthenticated
                    ? false
                    : accessiblePlatforms.some((p) => p.id === platform.id)
                }
              />
            ))}
          </div>
        </div>

        <PillarsBar />

        {/* ── Section panel (card → panel morph) ───────────────────────────── */}
        <AnimatePresence>
          {openPlatform && (
            <SectionPanel
              key={openPlatform.id}
              platform={openPlatform}
              onClose={closeSection}
            />
          )}
        </AnimatePresence>

        {/* Footer */}
        <motion.footer
          className="px-6 md:px-10 py-4 flex flex-wrap items-center justify-between gap-3 border-t border-white/10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
        >
          <span className="text-2xs font-mono text-white/60">
            © 2025 GroundLink Handling · intranet.groundlinkhandling.pt
          </span>
          <TimeOfDayControl mode={timeOfDay.mode} setMode={timeOfDay.setMode} />
          <span className="text-2xs font-mono" style={{ color: '#4AAE49' }}>
            {new Date().toLocaleTimeString('en-GB')} UTC+1
          </span>
        </motion.footer>
      </div>
    </div>
  );
}
